import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FirebaseContext } from 'common';
import { ref as databaseRef, onValue, push, set, update, remove } from 'firebase/database';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import { colors } from '../components/Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';

// Images par type de véhicule (depuis /cartypes Firebase)
const CAR_TYPE_IMAGES = {
  'Confort':        'https://firebasestorage.googleapis.com/v0/b/allodriver-9139a.firebasestorage.app/o/cartypes%2Ftype2?alt=media&token=f8432584-7b2f-40e0-b6c3-c1786cbc81a5',
  'Van':            'https://firebasestorage.googleapis.com/v0/b/allodriver-9139a.firebasestorage.app/o/cartypes%2Ftype3?alt=media&token=9a49ddd1-591a-491d-8e96-e620fb02429e',
  'Confort Women':  'https://firebasestorage.googleapis.com/v0/b/allodriver-9139a.firebasestorage.app/o/cartypes%2F-OfZI9Gc9-EAvUgSj8Bh?alt=media&token=f23e83b7-6b00-413b-8fd1-5535856df74d'
};

// Construit l'entrée /cars/{ghostId} attendue par l'app mobile
const buildCarMirror = (ghostId, data) => ({
  active:       true,
  approved:     true,
  carType:      data.carType || 'Confort',
  car_image:    CAR_TYPE_IMAGES[data.carType] || CAR_TYPE_IMAGES['Confort'],
  driver:       ghostId,
  driverName:   data.name || 'Chauffeur fantome',
  id:           ghostId,
  vehicleMake:  data.vehicleMake || '',
  vehicleModel: data.vehicleModel || '',
  vehicleNumber:data.vehicleNumber || '',
  source:       'ghost_driver'
});

// Construit l'entrée /users/{ghostId} attendue par l'app mobile
const buildUserMirror = (data) => ({
  driverActiveStatus: Boolean(data.active),
  approved:           true,
  carApproved:        true,
  // Requis car license_image_required, carType_required et term_required sont activés
  licenseImage:       'ghost_driver',
  term:               true,
  queue:              false,
  usertype:           'driver',
  carType:            data.carType || 'Confort',
  vehicleNumber:      data.vehicleNumber || '',
  firstName:          (data.name || 'Fantome').split(' ')[0],
  lastName:           (data.name || '').split(' ').slice(1).join(' '),
  source:             'ghost_driver'
});

// Construit l'entrée /locations/{ghostId} attendue par l'app mobile
const buildLocationMirror = (data) => {
  const loc = data.location || { lat: Number(data.lat || 0), lng: Number(data.lng || 0) };
  return {
    lat: loc.lat,
    lng: loc.lng,
    lastLocationUpdate: Date.now()
  };
};

const DEFAULT_FORM = {
  name: '',
  city: 'Toulouse',
  vehicleMake: 'Renault',
  vehicleModel: 'Talisman',
  vehicleColor: 'Noir',
  vehicleNumber: '',
  carType: 'Confort',
  lat: '43.6045',
  lng: '1.4440',
  radiusKm: '2.5',
  active: true,
  visibleOnDashboard: true
};

const CITY_PRESETS = {
  Toulouse: { lat: 43.6045, lng: 1.4440, radiusKm: 3 },
  Blagnac: { lat: 43.6350, lng: 1.3906, radiusKm: 2.2 },
  Montauban: { lat: 44.0183, lng: 1.3543, radiusKm: 2.5 }
};

const randomOffset = (radiusKm = 2) => {
  const radiusInDegrees = Number(radiusKm || 2) / 111;
  const angle = Math.random() * Math.PI * 2;
  const distance = Math.sqrt(Math.random()) * radiusInDegrees;
  return {
    lat: Math.cos(angle) * distance,
    lng: Math.sin(angle) * distance
  };
};

const buildGhostPayload = (form) => {
  const lat = Number(form.lat);
  const lng = Number(form.lng);
  const radiusKm = Number(form.radiusKm || 2);
  const now = Date.now();
  return {
    name: form.name.trim(),
    city: form.city.trim(),
    vehicleMake: form.vehicleMake.trim(),
    vehicleModel: form.vehicleModel.trim(),
    vehicleColor: form.vehicleColor.trim(),
    vehicleNumber: form.vehicleNumber.trim().toUpperCase(),
    carType: form.carType.trim(),
    active: Boolean(form.active),
    visibleOnDashboard: Boolean(form.visibleOnDashboard),
    acceptsBookings: false,
    queueLocked: false,
    source: 'admin_ghost_driver',
    location: {
      lat,
      lng,
      lastLocationUpdate: now
    },
    zone: {
      centerLat: lat,
      centerLng: lng,
      radiusKm
    },
    updatedAt: now
  };
};

const GhostDrivers = () => {
  const firebase = useContext(FirebaseContext);
  const [ghostDrivers, setGhostDrivers] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!firebase || !firebase.database) return;
    const ghostRef = databaseRef(firebase.database, 'ghostDrivers');
    const unsubscribe = onValue(ghostRef, snapshot => {
      const data = snapshot.val() || {};
      setGhostDrivers(Object.entries(data).map(([id, value]) => ({ id, ...value })));
    });
    return () => unsubscribe();
  }, [firebase]);

  const activeCount = useMemo(() => ghostDrivers.filter(driver => driver.active).length, [ghostDrivers]);

  const setField = (field, value) => {
    if (field === 'city' && CITY_PRESETS[value]) {
      const preset = CITY_PRESETS[value];
      setForm(current => ({
        ...current,
        city: value,
        lat: String(preset.lat),
        lng: String(preset.lng),
        radiusKm: String(preset.radiusKm)
      }));
      return;
    }
    setForm(current => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setForm(DEFAULT_FORM);
    setEditingId(null);
  };

  const saveGhost = async () => {
    if (!firebase || !firebase.database) return;
    if (!form.name.trim() || !form.vehicleNumber.trim() || !Number.isFinite(Number(form.lat)) || !Number.isFinite(Number(form.lng))) {
      setMessage('Nom, immatriculation et coordonnees GPS valides sont obligatoires.');
      return;
    }
    const payload = buildGhostPayload(form);
    if (editingId) {
      await update(databaseRef(firebase.database, `ghostDrivers/${editingId}`), payload);
      await update(databaseRef(firebase.database, `cars/${editingId}`), buildCarMirror(editingId, payload));
      await update(databaseRef(firebase.database, `users/${editingId}`), buildUserMirror(payload));
      await set(databaseRef(firebase.database, `locations/${editingId}`), buildLocationMirror(payload));
      setMessage('Chauffeur fantome mis a jour.');
    } else {
      const fullPayload = { ...payload, createdAt: Date.now() };
      const newRef = push(databaseRef(firebase.database, 'ghostDrivers'));
      const ghostId = newRef.key;
      await set(newRef, fullPayload);
      await set(databaseRef(firebase.database, `cars/${ghostId}`), buildCarMirror(ghostId, fullPayload));
      await set(databaseRef(firebase.database, `users/${ghostId}`), buildUserMirror(fullPayload));
      await set(databaseRef(firebase.database, `locations/${ghostId}`), buildLocationMirror(fullPayload));
      setMessage('Chauffeur fantome cree.');
    }
    resetForm();
  };

  const editGhost = (driver) => {
    setEditingId(driver.id);
    setForm({
      name: driver.name || '',
      city: driver.city || '',
      vehicleMake: driver.vehicleMake || '',
      vehicleModel: driver.vehicleModel || '',
      vehicleColor: driver.vehicleColor || '',
      vehicleNumber: driver.vehicleNumber || '',
      carType: driver.carType || 'Confort',
      lat: String(driver.zone?.centerLat || driver.location?.lat || ''),
      lng: String(driver.zone?.centerLng || driver.location?.lng || ''),
      radiusKm: String(driver.zone?.radiusKm || 2),
      active: driver.active !== false,
      visibleOnDashboard: driver.visibleOnDashboard !== false
    });
  };

  const toggleGhost = async (driver, field) => {
    if (!firebase || !firebase.database) return;
    const newValue = !driver[field];
    await update(databaseRef(firebase.database, `ghostDrivers/${driver.id}`), {
      [field]: newValue,
      acceptsBookings: false,
      queueLocked: false,
      updatedAt: Date.now()
    });
    if (field === 'active') {
      await update(databaseRef(firebase.database, `users/${driver.id}`), { driverActiveStatus: newValue });
      // Rafraichir le timestamp de localisation pour que l'app detecte le chauffeur
      if (newValue) {
        await set(databaseRef(firebase.database, `locations/${driver.id}`), buildLocationMirror(driver));
      }
    }
  };

  const moveGhost = async (driver) => {
    if (!firebase || !firebase.database) return;
    const zone = driver.zone || {};
    const centerLat = Number(zone.centerLat || driver.location?.lat);
    const centerLng = Number(zone.centerLng || driver.location?.lng);
    const radiusKm = Number(zone.radiusKm || 2);
    const offset = randomOffset(radiusKm);
    const newLocation = {
      lat: Number((centerLat + offset.lat).toFixed(6)),
      lng: Number((centerLng + offset.lng).toFixed(6)),
      lastLocationUpdate: Date.now()
    };
    await update(databaseRef(firebase.database, `ghostDrivers/${driver.id}`), {
      location: newLocation,
      acceptsBookings: false,
      queueLocked: false,
      updatedAt: Date.now()
    });
    // Synchroniser la position dans /locations pour l'app mobile
    await set(databaseRef(firebase.database, `locations/${driver.id}`), newLocation);
  };

  const deleteGhost = async (driver) => {
    if (!firebase || !firebase.database) return;
    await remove(databaseRef(firebase.database, `ghostDrivers/${driver.id}`));
    await remove(databaseRef(firebase.database, `cars/${driver.id}`));
    await remove(databaseRef(firebase.database, `users/${driver.id}`));
    await remove(databaseRef(firebase.database, `locations/${driver.id}`));
    setMessage('Chauffeur fantome supprime.');
  };

  // Synchronise les ghosts existants + rafraichit /locations pour tous les actifs
  const syncAllGhosts = async () => {
    if (!firebase || !firebase.database) return;
    let synced = 0;
    for (const driver of ghostDrivers) {
      await set(databaseRef(firebase.database, `cars/${driver.id}`), buildCarMirror(driver.id, driver));
      await set(databaseRef(firebase.database, `users/${driver.id}`), buildUserMirror(driver));
      if (driver.active !== false) {
        await set(databaseRef(firebase.database, `locations/${driver.id}`), buildLocationMirror(driver));
      }
      synced++;
    }
    setMessage(`${synced} chauffeur(s) fantome synchronise(s) et visibles dans l\'app.`);
  };

  // Heartbeat : rafraichit lastLocationUpdate des ghosts actifs toutes les 10 min
  // (l'app filtre les chauffeurs inactifs depuis > 15 min)
  useEffect(() => {
    if (!firebase || !firebase.database) return;
    const interval = setInterval(async () => {
      const actives = ghostDrivers.filter(d => d.active !== false);
      for (const driver of actives) {
        await update(databaseRef(firebase.database, `locations/${driver.id}`), { lastLocationUpdate: Date.now() });
      }
    }, 10 * 60 * 1000); // toutes les 10 minutes
    return () => clearInterval(interval);
  }, [firebase, ghostDrivers]);

  return (
    <Box sx={{ p: 2, fontFamily: FONT_FAMILY }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontFamily: FONT_FAMILY, fontWeight: 800 }}>Chauffeurs fantomes</Typography>
          <Typography sx={{ color: '#667085', fontFamily: FONT_FAMILY }}>
            Chauffeurs visibles sur la carte et dans la liste de l\'app. Miroir automatique dans /cars et /users.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`${activeCount} actifs`} color="success" />
          <Chip label={`${ghostDrivers.length} total`} />
          <Button variant="outlined" size="small" onClick={syncAllGhosts}>Synchroniser tout</Button>
        </Stack>
      </Stack>

      {message ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert> : null}

      <Card sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}><TextField fullWidth label="Nom" value={form.name} onChange={event => setField('name', event.target.value)} /></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth select SelectProps={{ native: true }} label="Ville" value={form.city} onChange={event => setField('city', event.target.value)}>{Object.keys(CITY_PRESETS).map(city => <option key={city} value={city}>{city}</option>)}</TextField></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth label="Marque" value={form.vehicleMake} onChange={event => setField('vehicleMake', event.target.value)} /></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth label="Modele" value={form.vehicleModel} onChange={event => setField('vehicleModel', event.target.value)} /></Grid>
          <Grid item xs={12} md={1.5}><TextField fullWidth label="Couleur" value={form.vehicleColor} onChange={event => setField('vehicleColor', event.target.value)} /></Grid>
          <Grid item xs={12} md={1.5}><TextField fullWidth label="Plaque" value={form.vehicleNumber} onChange={event => setField('vehicleNumber', event.target.value)} /></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth label="Type" value={form.carType} onChange={event => setField('carType', event.target.value)} /></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth label="Latitude" value={form.lat} onChange={event => setField('lat', event.target.value)} /></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth label="Longitude" value={form.lng} onChange={event => setField('lng', event.target.value)} /></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth label="Rayon km" value={form.radiusKm} onChange={event => setField('radiusKm', event.target.value)} /></Grid>
          <Grid item xs={12} md={2}><Stack direction="row" alignItems="center" spacing={1}><Switch checked={form.active} onChange={event => setField('active', event.target.checked)} /><Typography>Actif</Typography></Stack></Grid>
          <Grid item xs={12} md={2}><Stack direction="row" alignItems="center" spacing={1}><Switch checked={form.visibleOnDashboard} onChange={event => setField('visibleOnDashboard', event.target.checked)} /><Typography>Dashboard</Typography></Stack></Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" startIcon={editingId ? <SaveIcon /> : <AddIcon />} onClick={saveGhost} sx={{ backgroundColor: colors.GREEN }}>{editingId ? 'Enregistrer' : 'Ajouter'}</Button>
              <Button variant="outlined" onClick={resetForm}>Annuler</Button>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      <TableContainer component={Card} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Ville</TableCell>
              <TableCell>Vehicule</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Actif</TableCell>
              <TableCell>Dashboard</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ghostDrivers.map(driver => (
              <TableRow key={driver.id} hover>
                <TableCell>{driver.name}</TableCell>
                <TableCell>{driver.city}</TableCell>
                <TableCell>{driver.vehicleMake} {driver.vehicleModel} {driver.vehicleColor}<br/><small>{driver.vehicleNumber} - {driver.carType}</small></TableCell>
                <TableCell>{driver.location?.lat}, {driver.location?.lng}<br/><small>{driver.location?.lastLocationUpdate ? new Date(driver.location.lastLocationUpdate).toLocaleString('fr-FR') : '-'}</small></TableCell>
                <TableCell><Switch checked={driver.active !== false} onChange={() => toggleGhost(driver, 'active')} /></TableCell>
                <TableCell><Switch checked={driver.visibleOnDashboard !== false} onChange={() => toggleGhost(driver, 'visibleOnDashboard')} /></TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" onClick={() => editGhost(driver)}>Modifier</Button>
                    <Button size="small" variant="outlined" startIcon={<MyLocationIcon />} onClick={() => moveGhost(driver)}>Deplacer</Button>
                    <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => deleteGhost(driver)}>Supprimer</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {ghostDrivers.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">Aucun chauffeur fantome configure.</TableCell></TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default GhostDrivers;